/**
 * Fast-forward (4x–16x) support shared by every recording player.
 *
 * ─── Why 8x/16x stuttered on every chunk ───
 * hls.js measures its buffer target in MEDIA seconds (maxBufferLength: 30) and loads one fragment
 * at a time. At 16x, 30 media seconds last under two real seconds, and each 2–6 s chunk plays out
 * in 0.1–0.4 s — about the time one chunk request takes, longer through the cloud tunnel or while
 * an H265 segment is being transcoded. So the element ran dry at nearly every chunk boundary:
 * play a fraction of a second, "Buffering…", play, "Buffering…".
 *
 * Three fixes, all of them no-ops at ≤ 2x so normal playback is exactly as before:
 *
 *   1. rateBufferConfig — scale hls.js's look-ahead with the speed, so it keeps loading well
 *      ahead instead of stopping 30 media seconds out.
 *   2. HighSpeedBufferGate — hysteresis. When the look-ahead drops below a LOW mark, hold
 *      playback and let the buffer fill to a HIGH mark (a few real seconds of play at the current
 *      speed), then release. The viewer gets one short wait and then a long smooth run, instead
 *      of a stall per chunk. It holds with playbackRate = 0, not pause(): the pages treat a
 *      `pause` event as the USER pausing and would flip their play button and play intent (Shaka
 *      Player buffers the same way for the same reason).
 *   3. attachNextSegmentWarmer — a recording is stitched from 5-minute segments, and the node
 *      only starts converting a segment when the first chunk of it is requested. At 16x a segment
 *      lasts ~19 s, so every boundary was a cold start (an H265 transcode start-up, or a remux)
 *      on the critical path. The warmer requests the next segment's first chunk as soon as hls.js
 *      starts loading the current one, so the conversion is already done when hls.js gets there.
 *
 *      It runs under its OWN session id (the player's id + "w"), never the player's. The
 *      playback service keeps the 2 most recently requested segments per session and cancels
 *      what falls out (SessionTranscodeTracker). Sharing the player's id, a boundary looked like
 *      three segments in flight — the one just finished, the one starting, the one being warmed
 *      — and the service cancelled the one STARTING, i.e. the segment about to be watched
 *      (seen 2026-09-30: that cancel then wedged the transcode and the wall buffered for
 *      minutes). A separate id cannot evict anything the player holds: a segment is only
 *      cancelled when no other session is on it.
 *
 *   4. Keyframe-only playback ("trick play") from TRICK_MIN_RATE up. Even with all of the above,
 *      8x means fetching and DECODING 8 seconds of every camera per second — 200 frames/s per
 *      tile at 25 fps — which a six-camera wall cannot sustain. From 8x the player asks for the
 *      keyframe-only version of each chunk (&k=1; the node's trick_play): same time window,
 *      same timestamps, one picture per camera GOP. The timeline is untouched and the element
 *      keeps playing at 8x/16x; it just holds each picture until the next keyframe — a smooth
 *      time-lapse at 1/25–1/50 of the decode work. Crossing the threshold swaps rendition in
 *      place (switchRendition): flush the buffer and reload from the playhead, the same thing
 *      hls.js does for an immediate quality switch — no source reload, so no `pause`/`emptied`
 *      events and no jump in the pages' timelines.
 *
 * The Recorded wall does not use the gate: its tiles already hold the shared clock while any of
 * them is buffering, so they use the same watermarks (bufferedAhead + highSpeedMarks) to decide
 * when they are "ready" instead — the whole wall then pauses once, together, and runs smoothly.
 */

/**
 * The slice of an hls.js instance used here. Structural on purpose: each dashboard bundles its
 * own hls.js (1.5.x local, 1.6.x cloud) and this library another, so naming the class would tie
 * the types to whichever copy happened to resolve.
 */
export interface HlsLike {
  config: { maxBufferLength: number };
  levels: Array<{ details?: { fragments: Array<{ url: string; sn: number | string }> } }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: any, listener: any): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: any, listener: any): void;
}

/** From this speed chunks are requested keyframe-only (see the header, point 4). */
export const TRICK_MIN_RATE = 8;
export const isTrickRate = (rate: number) => rate >= TRICK_MIN_RATE;

/** `url` with the keyframe-only flag when `on`. Only recording-segment chunks have a keyframe
 *  rendition; the still-recording tail ("/live/") and playlists are left alone. */
export function trickChunkUrl(url: string, on: boolean): string {
  if (!on || !url.includes('/hls/') || !url.includes('.ts') || url.includes('/live/') || /[?&]k=1(&|$)/.test(url)) {
    return url;
  }
  return `${url}${url.includes('?') ? '&' : '?'}k=1`;
}

/** At or below this speed nothing here changes behaviour. */
export const HIGH_SPEED_MIN_RATE = 2;

/** Real seconds of play the gate fills before releasing, and the real-seconds low mark. */
const HIGH_WALL_S = 3;
const LOW_WALL_S = 0.6;
/** Real seconds of look-ahead hls.js is asked to keep loaded at high speed. */
const LOOKAHEAD_WALL_S = 8;

export const isHighSpeed = (rate: number) => rate > HIGH_SPEED_MIN_RATE;

/** hls.js buffer settings for a playback speed. At ≤ 2x these are the players' existing values. */
export function rateBufferConfig(rate: number) {
  if (!isHighSpeed(rate)) {
    return { maxBufferLength: 30, maxMaxBufferLength: 60, maxBufferSize: 60 * 1000 * 1000, backBufferLength: 30 };
  }
  const ahead = Math.min(240, Math.max(30, rate * LOOKAHEAD_WALL_S));
  return {
    maxBufferLength: ahead,
    maxMaxBufferLength: Math.min(480, ahead * 2),
    // Bytes, not seconds: 128 media seconds of a 4 Mbps camera is ~64 MB. Capped below the
    // browsers' MSE quota (~150 MB of video in Chrome); hls.js handles a quota error by backing off.
    maxBufferSize: Math.min(120, 60 * Math.ceil(rate / 8)) * 1000 * 1000,
    // What is behind the playhead at 16x is gone in seconds — keep less of it, so the quota
    // goes to what is ahead.
    backBufferLength: 10,
  };
}

/** What switchRendition needs from hls.js (structural, like HlsLike). */
export interface HlsReloadable {
  stopLoad(): void;
  startLoad(startPosition?: number): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger(event: any, data: any): unknown;
}

/**
 * Swap to the other rendition (normal ↔ keyframe-only) in place: stop loading, flush the whole
 * buffer, and load again from the playhead. The caller has already flipped the flag its
 * xhrSetup reads, so every request from here on is for the new rendition. This is hls.js's own
 * immediate level switch (abort + flush [0, ∞)); when the flush lands, its fragment tracker
 * drops the flushed fragments and the stream controller reloads from the current position.
 */
export function switchRendition(hls: HlsReloadable, bufferFlushingEvent: string, video: HTMLVideoElement) {
  const pos = video.currentTime;
  try {
    hls.stopLoad();
    hls.trigger(bufferFlushingEvent, { startOffset: 0, endOffset: Number.POSITIVE_INFINITY, type: null });
    hls.startLoad(pos);
  } catch { /* torn down meanwhile — the next source starts in the right mode anyway */ }
}

/** Apply rateBufferConfig to a live hls.js instance. The stream controller re-reads these on
 *  every tick, so a speed change takes effect without reloading the source. */
export function applyRateToHls(hls: HlsLike | null | undefined, rate: number) {
  if (!hls) return;
  Object.assign(hls.config, rateBufferConfig(rate));
}

/** Media seconds buffered ahead of the playhead (0 when the playhead is in a hole). */
export function bufferedAhead(video: HTMLVideoElement): number {
  const t = video.currentTime;
  const b = video.buffered;
  for (let i = 0; i < b.length; i++) {
    if (b.start(i) <= t + 0.3 && b.end(i) > t) return b.end(i) - t;
  }
  return 0;
}

/** True when everything up to the end of the media is already buffered (nothing to wait for). */
export function bufferedToEnd(video: HTMLVideoElement): boolean {
  const d = video.duration;
  return Number.isFinite(d) && d > 0 && video.currentTime + bufferedAhead(video) >= d - 0.3;
}

/** LOW/HIGH marks in media seconds for a speed. `cap` is the most hls.js will load ahead. */
export function highSpeedMarks(rate: number, cap = rateBufferConfig(rate).maxBufferLength) {
  const high = Math.min(rate * HIGH_WALL_S, cap * 0.8);
  const low = Math.min(Math.max(1.5, rate * LOW_WALL_S), high * 0.5);
  return { low, high };
}

export interface GateState {
  holding: boolean;
  /** 0–1 progress towards the release mark while holding. */
  progress: number;
}

/**
 * Hysteresis buffering for one <video> at high speed. See the header.
 *
 * The owner routes EVERY rate change through setRate() — the gate owns element.playbackRate
 * while it is enabled — and destroys it with the source.
 */
export class HighSpeedBufferGate {
  private rate = 1;
  private holding = false;
  private lastAhead = 0;
  private lastGrowth = 0;
  private timer: number;
  private state: GateState = { holding: false, progress: 1 };

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly onChange?: (s: GateState) => void,
    /** Current hls.js look-ahead cap, so the release mark is never above what can be loaded. */
    private readonly lookaheadCap?: () => number | undefined,
  ) {
    this.timer = window.setInterval(() => this.tick(), 200);
  }

  setRate(rate: number) {
    this.rate = rate;
    if (!isHighSpeed(rate)) this.release();
    else if (!this.holding) this.video.playbackRate = rate;
  }

  get isHolding() { return this.holding; }

  destroy() {
    window.clearInterval(this.timer);
    this.release();
  }

  private release() {
    const wasHolding = this.holding;
    this.holding = false;
    if (Math.abs(this.video.playbackRate - this.rate) > 0.001) this.video.playbackRate = this.rate;
    if (wasHolding || this.state.holding) this.emit({ holding: false, progress: 1 });
  }

  private emit(s: GateState) {
    if (s.holding === this.state.holding && Math.abs(s.progress - this.state.progress) < 0.05) return;
    this.state = s;
    this.onChange?.(s);
  }

  private tick() {
    const v = this.video;
    // Paused by the user, or not fast: nothing to manage.
    if (v.paused || v.ended || !isHighSpeed(this.rate)) {
      if (this.holding) this.release();
      return;
    }
    const now = performance.now();
    const ahead = bufferedAhead(v);
    if (ahead > this.lastAhead + 0.05) this.lastGrowth = now;
    this.lastAhead = ahead;
    const { low, high } = highSpeedMarks(this.rate, this.lookaheadCap?.());

    if (!this.holding) {
      // Hold BEFORE the element runs dry (a stall at 16x shows as a frozen frame and a spinner
      // per chunk), unless the rest of the media is already buffered.
      if ((ahead < low || v.readyState < 3) && !bufferedToEnd(v) && !v.seeking) {
        this.holding = true;
        this.lastGrowth = now;
        v.playbackRate = 0;
        this.emit({ holding: true, progress: Math.min(1, ahead / high) });
      }
      return;
    }

    // Holding: release at the high mark, at the end of the media, or when the buffer has stopped
    // growing with at least the low mark in hand (hls.js hit its byte cap — waiting longer
    // would never reach the mark and would just freeze the picture).
    const stuck = now - this.lastGrowth > 1500 && ahead >= low;
    if (ahead >= high || bufferedToEnd(v) || stuck) {
      this.release();
    } else {
      if (v.playbackRate !== 0) v.playbackRate = 0;
      this.emit({ holding: true, progress: Math.min(1, ahead / high) });
    }
  }
}

/**
 * Warm the NEXT recording segment's conversion while the current one is loading. See the header.
 *
 * `request` turns a fragment URL into the fetch to make: the player's Authorization, and a
 * session id of the warmer's OWN (see the header — never the player's, or it evicts the player's
 * current segment on the server).
 */
export function attachNextSegmentWarmer(
  hls: HlsLike,
  /** `Hls.Events.FRAG_LOADING` from the caller's own hls.js. */
  fragLoadingEvent: string,
  getRate: () => number,
  request: (url: string) => { url: string; headers: Record<string, string> },
): () => void {
  const warmed = new Set<string>();
  const inflight = new Set<AbortController>();
  const segmentOf = (url: string) => {
    const i = url.lastIndexOf('/');
    return i > 0 ? url.slice(0, i).split('?')[0] : url;
  };

  const onFragLoading = (_e: unknown, data: { frag: { url: string; sn: number | string; level: number } }) => {
    if (!isHighSpeed(getRate())) return;
    const frag = data.frag;
    if (!frag?.url || typeof frag.sn !== 'number') return;
    const frags = hls.levels[frag.level]?.details?.fragments;
    if (!frags) return;
    const current = segmentOf(frag.url);
    const start = frags.findIndex((f) => f.sn === frag.sn);
    if (start < 0) return;
    // The first fragment AFTER the current segment — i.e. the start of the next one.
    const next = frags.slice(start + 1).find((f) => segmentOf(f.url) !== current);
    // The still-recording tail ("/live/") is served per fragment; there is nothing to warm.
    if (!next || next.url.includes('/live/')) return;
    const key = segmentOf(next.url);
    if (warmed.has(key)) return;
    warmed.add(key);
    const ctl = new AbortController();
    inflight.add(ctl);
    const r = request(next.url);
    // The body is discarded: the point is that the node converts the segment and caches the
    // chunk, so hls.js's own request for it is a cache hit. Errors are irrelevant — hls.js will
    // make the real request and handle whatever it gets.
    fetch(r.url, { headers: r.headers, signal: ctl.signal, cache: 'no-store' })
      .then((res) => res.arrayBuffer())
      .catch(() => { warmed.delete(key); })
      .finally(() => inflight.delete(ctl));
  };

  hls.on(fragLoadingEvent, onFragLoading);
  return () => {
    hls.off(fragLoadingEvent, onFragLoading);
    inflight.forEach((c) => c.abort());
    inflight.clear();
  };
}
